import React, { memo, useEffect, useState } from 'react';
import {
  Box,
  Cover,
  Stack,
  ButtonWithAction,
  Text,
  NavHeader,
  InternalLink,
  NavMenuDesktop,
  withWindowSize,
  util,
  constants,
  Cluster,
  Detail,
} from '@thecb/components';
import { connect, useDispatch, useSelector } from 'react-redux';
import eunaCashieringLogo from '../../static/img/euna-cashiering.svg';

import { openCashDrawer } from '../../state/peripheralDevices';
import { fallbackValues } from './Header.theme';
import { LOGOUT_REASONS, OKTA_LOGOUT_REASON_KEY } from '../../utils/constants';
import { logout } from '../../state/reducer';
import { can } from '../../utils/helpers.js';
import {
  fetchClientConfigRequest,
  fetchCurrentAbilitiesRequest,
} from '../../state/shared';
import { useLocation, useNavigate } from 'react-router-dom';

const {
  theme: { themeComponent },
} = util;

/**
 * TODO:
 * Update this entire component with dynamic POS specific user data.
 * It still contains references such values as firstname, lastname, email, etc.
 * Instead, it should have POS references such as cashier role, location, terminal.
 */
const CashierToolsMenu = ({
  themeValues = {},
  navMenuOpen,
  setNavMenuOpen,
}) => {
  let menuTimeoutID;
  const location = useLocation();
  const dispatch = useDispatch();

  const cashierToolsButton = (
    <Box
      padding="0"
      onMouseEnter={() => {
        if (menuTimeoutID) {
          clearTimeout(menuTimeoutID);
        }
        setNavMenuOpen(true);
      }}
      onMouseLeave={() => {
        menuTimeoutID = setTimeout(() => setNavMenuOpen(false), 400);
      }}
      onClick={() => {
        setNavMenuOpen(!navMenuOpen);
      }}
      ariaControls="navigation"
      ariaLabel="Menu"
      type="button"
    >
      <ButtonWithAction
        variant="tertiary"
        contentOverride
        extraStyles={`margin: 0; line-height: 0.5;`}
        textExtraStyles="line-height: 0.5;"
      >
        <Box padding="0">
          <Text
            variant="p"
            color={themeValues.linkColor}
            extraStyles={`display: inline-block; vertical-align: super;`}
          >
            Cashier Tools
          </Text>
        </Box>
      </ButtonWithAction>
    </Box>
  );

  const menuContent = (
    <Stack padding="0" childGap="0" bottomItem={``} role="navigation">
      <Box
        padding="0.5rem"
        hoverStyles={`background-color: ${constants.colors.LUCKY_POINT_DARK}`}
        key="cashdrop"
        minWidth="100%"
        onBlur={() =>
          (menuTimeoutID = setTimeout(() => setNavMenuOpen(false), 200))
        }
      >
        <InternalLink
          to="/pos/cashdrop"
          variant="whitePrimary"
          extraStyles={`&:hover { text-decoration: none }; text-decoration: none`}
        >
          <Text
            variant="p"
            color={themeValues.linkColor}
            weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
            extraStyles={`margin-left: 1rem; text-decoration: none`}
          >
            Cash Drop
          </Text>
        </InternalLink>
      </Box>
      <Box
        padding="0.5rem"
        hoverStyles={`background-color: ${constants.colors.LUCKY_POINT_DARK}`}
        key="closeout"
        minWidth="100%"
        onBlur={() =>
          (menuTimeoutID = setTimeout(() => setNavMenuOpen(false), 400))
        }
      >
        <InternalLink
          to="/pos/closeout"
          variant="whitePrimary"
          extraStyles={`&:hover { text-decoration: none }; text-decoration: none`}
        >
          <Text
            variant="p"
            color={themeValues.linkColor}
            weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
            extraStyles={`margin-left: 1rem; text-decoration: none`}
          >
            Closeout
          </Text>
        </InternalLink>
      </Box>
      <Box
        padding="0.5rem 0.5rem 0.5rem 0"
        hoverStyles={`background-color: ${constants.colors.LUCKY_POINT_DARK}`}
        key="open-drawer"
        onBlur={() =>
          (menuTimeoutID = setTimeout(() => setNavMenuOpen(false), 400))
        }
      >
        <ButtonWithAction
          padding="0"
          extraStyles={`margin: 0; background-color: transparent;`}
          textExtraStyles={`font-size: 1rem;`}
          variant="tertiary"
          text="Open Drawer"
          action={() => dispatch(openCashDrawer({ location }))}
        />
      </Box>
    </Stack>
  );
  return (
    <Box padding="0" extraStyles={`position: relative; z-index: 100;`}>
      {cashierToolsButton}
      <NavMenuDesktop
        top="100%"
        left="-100%"
        menuContent={menuContent}
        visible={navMenuOpen}
        onMouseLeave={() =>
          (menuTimeoutID = setTimeout(() => setNavMenuOpen(false), 400))
        }
        onMouseEnter={() => clearTimeout(menuTimeoutID)}
        onFocus={() => clearTimeout(menuTimeoutID)}
      />
    </Box>
  );
};

const Header = ({
  themeValues = {},
  isLoggedIn,
  userFirstName = '',
  userLastName = '',
  terminal,
  location,
  batchNumber,
  routing,
  hasTerminalSession,
}) => {
  const { currentAbilities } = useSelector((state) => state.shared);
  const { clientId } = useSelector((state) => state.terminalLogin);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (clientId) {
      dispatch(fetchClientConfigRequest(clientId));
    }
  }, [clientId]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchCurrentAbilitiesRequest());
    }
  }, [isLoggedIn]);

  const headerLogo = (
    <Box padding="0" minHeight="100%">
      <Cover minHeight="100%">
        <img
          src={eunaCashieringLogo}
          width="228px"
          height="40px"
          alt="Euna Cashiering"
        />
      </Cover>
    </Box>
  );

  const mainNavContent = (
    <Cluster align="center" direction="row" overflow="true">
      {isLoggedIn && (
        <>
          {hasTerminalSession && (
            <>
              <Box padding="0.5rem" key="home">
                <InternalLink
                  to="/pos"
                  variant="whitePrimary"
                  extraStyles={`&:hover { text-decoration: none }; text-decoration-color: ${themeValues.linkColor}; text-decoration: none `}
                >
                  <Text
                    variant="p"
                    color={themeValues.linkColor}
                    extraStyles={`margin-left: 1rem; text-decoration: none`}
                  >
                    Home
                  </Text>
                </InternalLink>
              </Box>
              {can('show', 'reporting_navigation_link', currentAbilities) && (
                <Box padding="0.5rem" key="reports">
                  <InternalLink
                    to="/pos/reporting"
                    variant="whitePrimary"
                    extraStyles={`&:hover { text-decoration: none }; text-decoration-color: ${themeValues.linkColor}; text-decoration: none `}
                  >
                    <Text
                      variant="p"
                      color={themeValues.linkColor}
                      extraStyles={`margin-left: 1rem; text-decoration: none`}
                    >
                      Reporting
                    </Text>
                  </InternalLink>
                </Box>
              )}
              <Box padding="0.5rem" key="orders">
                <InternalLink
                  to="/pos/orders"
                  variant="whitePrimary"
                  extraStyles={`&:hover { text-decoration: none }; text-decoration-color: ${themeValues.linkColor}; text-decoration: none `}
                >
                  <Text
                    variant="p"
                    color={themeValues.linkColor}
                    extraStyles={`margin-left: 1rem; text-decoration: none`}
                  >
                    Orders
                  </Text>
                </InternalLink>
              </Box>
              <Box padding="0.5rem" key="drawer-history">
                <InternalLink
                  to="/pos/drawer-history"
                  variant="whitePrimary"
                  extraStyles={`&:hover { text-decoration: none }; text-decoration-color: ${themeValues.linkColor}; text-decoration: none`}
                >
                  <Text
                    variant="p"
                    color={themeValues.linkColor}
                    extraStyles={`margin-left: 1rem; text-decoration: none `}
                  >
                    Drawer History
                  </Text>
                </InternalLink>
              </Box>
              <CashierToolsMenu
                themeValues={themeValues}
                routing={routing}
                navMenuOpen={navMenuOpen}
                setNavMenuOpen={setNavMenuOpen}
              />
              <Detail color={themeValues.linkColor}>{' | '}</Detail>
            </>
          )}
          <Box padding="0.5rem 0" minWidth="0" key="logout">
            <ButtonWithAction
              minWidth="0"
              variant="tertiary"
              text="Log Out"
              textExtraStyles={`
            font-size: 1rem;
            font-weight: normal;
          `}
              action={() => {
                setNavMenuOpen(false);
                localStorage.setItem(
                  OKTA_LOGOUT_REASON_KEY,
                  LOGOUT_REASONS.USER_INITIATED.name,
                );
                dispatch(logout({ navigate }));
              }}
            />
          </Box>
        </>
      )}
    </Cluster>
  );

  const subHeaderLeftContent = (
    <Cluster align="center" childGap="0.5rem" overflow="visible" margin="0">
      <Detail margin="none" extraStyles={`font-size: 1rem;`}>
        Cashier:
      </Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`} weight="700">
        {userFirstName} {userLastName}
      </Detail>
      <Detail margin="none">|</Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`}>
        Location:
      </Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`} weight="700">
        {location}
      </Detail>
      <Detail margin="none">|</Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`}>
        Terminal:
      </Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`} weight="700">
        {terminal}
      </Detail>
      <Detail margin="none">|</Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`}>
        Batch No:
      </Detail>
      <Detail margin="none" extraStyles={`font-size: 1rem;`} weight="700">
        {batchNumber}
      </Detail>
    </Cluster>
  );

  return (
    <>
      <Box padding="0" role="banner">
        <NavHeader
          headerHeight="104px"
          key="header"
          backgroundColor={themeValues.backgroundColor}
          leftContent={headerLogo}
          rightContent={mainNavContent}
          extraStyles={`z-index: 0.5`}
          headerWidth="85.714rem"
        />
        {isLoggedIn && (
          <NavHeader
            key="sub-header"
            leftContent={subHeaderLeftContent}
            backgroundColor={themeValues.linkColor}
            role="contentinfo"
            aria-label="subheader"
            headerHeight="54px"
            headerWidth="85.714rem"
            extraStyles={`z-index: 1; box-shadow: 0px 1px 2px -2px #292A331A, 0px 3px 6px -1px #292A3354, 0px 1px 0px 0px #E5E7EC inset;
            `}
          />
        )}
      </Box>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    userFirstName: state?.login?.cashier?.first_name ?? '',
    userLastName: state?.login?.cashier?.last_name ?? '',
    userRole: state?.login?.cashier?.role ?? '',
    terminal: state?.terminalLogin?.terminalSession?.terminal ?? '',
    location: state?.terminalLogin?.terminalSession?.location ?? '',
    batchNumber: state?.terminalLogin?.terminalSession?.uuid ?? '',
  };
};

export default connect(mapStateToProps)(
  withWindowSize(memo(themeComponent(Header, 'Header', fallbackValues))),
);
